import { unitDef } from '../content/units';
import { buildingDef } from '../content/buildings';
import { projectileDef } from '../content/combat';
import { UnitMode } from '../sim/types';
import type { GameState, Unit, UnitId } from '../sim/types';
import { worldToScreenX, worldToScreenY } from './projection';
import type { Camera, Viewport } from './projection';
import type { EnemyView, RememberedBuilding } from './memory';
import { ROTATIONS, buildingSprites } from './atlas';
import type { SpriteAtlas } from './atlas';
import { TILE } from '../sim/constants';

/*
 * ציור היחידות.
 *
 * שני דברים שקורים כאן ולא בסימולציה:
 *   1. **שזירה בין טיקים.** הסימולציה רצה ב-20Hz; הציור ב-60. המיקום המצויר הוא
 *      שילוב של `prev` ו-`cur` לפי alpha, ולכן התנועה חלקה בלי להאיץ את הסימולציה.
 *   2. **כיוון הפנייה.** הוא נגזר מכיוון התנועה ונשמר כאן, מחוץ למצב. במכוון:
 *      ב-M2 הפנייה קוסמטית בלבד, ולהכניס אותה למצב היה מוסיף שדה שנכנס לגיבוב
 *      ולשמירה בלי שאף כלל משחק תלוי בו. כשצריחים ידרשו זמן סיבוב אמיתי — היא
 *      תעבור לסימולציה, וזה יהיה שינוי מודע ולא הרגל.
 */

/** כיוון אחרון ידוע לכל יחידה, כדי שיחידה שעצרה לא תקפוץ לכיוון 0. */
const facings = new Map<UnitId, number>();

export function resetFacings(): void {
    facings.clear();
}

function facingOf(unit: Unit, previous: Unit | undefined): number {
    const dx = previous ? unit.x - previous.x : 0;
    const dy = previous ? unit.y - previous.y : 0;
    if (dx !== 0 || dy !== 0) {
        const angle = Math.atan2(dy, dx);
        const index = Math.round((angle / (Math.PI * 2)) * ROTATIONS);
        const normalized = ((index % ROTATIONS) + ROTATIONS) % ROTATIONS;
        facings.set(unit.id, normalized);
        return normalized;
    }
    return facings.get(unit.id) ?? 0;
}

export interface DrawUnitsOptions {
    readonly cur: GameState;
    readonly prev: GameState;
    readonly alpha: number;
    readonly atlas: SpriteAtlas;
    readonly playerColors: readonly string[];
    readonly selected: ReadonlySet<UnitId>;
    readonly localPlayer: number;
    readonly fog?: FogView;
}

/**
 * מה ששכבת הציור צריכה לדעת על ערפל הקרב. מסכות גולמיות ולא אובייקט חכם — הן
 * מגיעות מ-`sim/vision.ts` ומ-`PlayerState.explored` כמו שהן.
 */
export interface FogView {
    readonly localPlayer: number;
    readonly mapWidth: number;
    /** נראה עכשיו. */
    readonly visible: Uint8Array;
    /** נחשף אי־פעם. */
    readonly explored: Uint8Array;
    /**
     * מבני האויב **כפי שהשחקן זוכר אותם**. ראו `render/memory.ts`: בלי זה
     * הבסיס שסיירת בו פעם אחת מתעדכן חי לנצח, וזו דליפת מודיעין.
     */
    readonly remembered?: ReadonlyMap<number, RememberedBuilding>;
    /**
     * יחידות אויב כפי שהשחקן **יודע** אותן — מיקום, שקיפות, והאם זו ידיעה ישנה.
     * ראו `render/memory.ts`. מי שאינו במפה אינו מצויר כלל.
     */
    readonly enemyAlpha?: ReadonlyMap<number, EnemyView>;
}

export function drawUnits(
    ctx: CanvasRenderingContext2D,
    cam: Camera,
    vp: Viewport,
    options: DrawUnitsOptions,
): number {
    const { cur, prev, alpha, atlas, playerColors, selected, fog } = options;

    let drawn = 0;

    /*
     * מפה לפי מזהה במקום מיזוג דו-מצביעים.
     *
     * המיזוג נשען על כך ששני המערכים ממוינים לפי מזהה, וזה נשבר ברגע שהציור עובר
     * לסדר אחר. מפה עולה אותו סדר גודל ולא אכפת לה מהסדר.
     */
    const previousById = new Map(prev.units.map((unit) => [unit.id, unit]));

    /*
     * מיון לפי y לפני הציור.
     *
     * בלעדיו הסדר הוא סדר המזהים, ולכן יחידה שעומדת **מאחור** יכולה להיות מצוירת
     * מעל מי שלפניה — וכששתיהן חופפות זה נקרא כמו באג. הסדר יציב: שוויון נשבר
     * לפי מזהה, ולכן שתי יחידות באותו גובה לא מהבהבות ביניהן.
     *
     * ציור בלבד — לסימולציה אין מושג מה נמצא "לפני" מה.
     */
    /*
     * מיון לפי y, ו**אוויר תמיד אחרי קרקע**: מסוק שנבלע מתחת לטנק שנמצא צפונה
     * ממנו הורס את כל האשליה של גובה. שבירת השוויון נשארת לפי מזהה, ולכן הסדר
     * עדיין יציב ושתי יחידות באותו גובה אינן מהבהבות.
     */
    const layerRank = (unit: { type: number }) => (unitDef(unit.type).layer === 'air' ? 1 : 0);
    const order = [...cur.units].sort(
        (a, b) => layerRank(a) - layerRank(b) || a.y - b.y || a.id - b.id,
    );

    for (const unit of order) {
        let fade = 1;
        /*
         * **ידיעה ישנה מצוירת במקום שבו נראתה לאחרונה, לא במקום שבו היא באמת.**
         * ראו `render/memory.ts`: יחידת אויב שיצאה מהטווח אינה נעלמת אלא נשארת
         * ודוהה. זה מה שהחליף את שכבת ההצללה שגרמה למפה לזוז בלי הרף.
         */
        let known: EnemyView | undefined;
        if (fog && unit.owner !== fog.localPlayer) {
            if (fog.enemyAlpha) {
                known = fog.enemyAlpha.get(unit.id);
                if (known === undefined) continue;
                fade = known.alpha;
            } else {
                const tile =
                    Math.floor(unit.y / TILE) * fog.mapWidth + Math.floor(unit.x / TILE);
                if (fog.visible[tile] !== 1) continue;
            }
        }

        const before = previousById.get(unit.id);

        /*
         * ידיעה ישנה אינה משוזרת בין טיקים — היא **קפואה**. שזירה כאן הייתה
         * גוררת אותה לעבר המיקום האמיתי, כלומר להראות תנועה שלא ראית.
         */
        const frozen = known?.stale === true ? known : undefined;
        const stale = frozen !== undefined;
        const worldX = frozen ? frozen.x : before ? before.x + (unit.x - before.x) * alpha : unit.x;
        const worldY = frozen ? frozen.y : before ? before.y + (unit.y - before.y) * alpha : unit.y;

        const screenX = worldToScreenX(worldX, cam, vp);
        const screenY = worldToScreenY(worldY, cam, vp);

        const def = unitDef(unit.type);
        const radiusPx = (def.radius / TILE) * cam.zoom;

        // Culling — יחידה מחוץ למסך אינה מצוירת בכלל.
        if (
            screenX + radiusPx < 0 ||
            screenY + radiusPx < 0 ||
            screenX - radiusPx > vp.width ||
            screenY - radiusPx > vp.height
        ) {
            continue;
        }

        /*
         * הדהייה חלה על **כל** ציור היחידה — גוף, צל ופס בריאות — ולא על הגוף
         * בלבד: צל מלא מתחת לטנק שקוף למחצה מסגיר בדיוק את מה שהדהייה מסתירה.
         */
        if (fade < 1) ctx.globalAlpha = fade;

        const color = playerColors[unit.owner] ?? playerColors[0] ?? '#ffffff';
        /*
         * ידיעה ישנה שומרת גם את **הפנייה** האחרונה: `facingOf` בלי `before`
         * מחזיר את ה-cache בלי לעדכן אותו. אחרת היחידה הייתה מסתובבת במקום —
         * הפנייה נגזרת מהתנועה האמיתית, שממשיכה מחוץ לטווח הראייה.
         */
        const facing = facingOf(unit, stale ? undefined : before);
        // גוף היחידה תופס כ-0.65 מרוחב תא ה-atlas, ולכן כדי שהגוף המצויר יהיה
        // בקוטר האמיתי של היחידה צריך לצייר את התא בערך ב-3 מהרדיוס.
        const size = radiusPx * 3.2;

        /*
         * צל.
         *
         * מצויר כאן ולא בתוך ה-atlas בכוונה: צל שנאפה לתוך הספרייט מסתובב יחד עם
         * היחידה, ואז נראה שמקור האור מסתובב סביב השדה. כאן הוא בקואורדינטות מסך
         * עם היסט קבוע, ולכן כל היחידות מטילות צל לאותו כיוון.
         */
        /*
         * **הצל של מטוס נופל רחוק ממנו, וזה כל מה שמסמן גובה במבט-על.**
         * בלי ההיסט הזה מסוק נראה כמו רכב מוזר שנוסע מעל מים.
         */
        const air = unitDef(unit.type).layer === 'air';
        const shadowDrop = air ? radiusPx * 1.35 : 0;
        ctx.fillStyle = air ? 'rgba(0, 0, 0, 0.24)' : 'rgba(0, 0, 0, 0.32)';
        ctx.beginPath();
        ctx.ellipse(
            screenX + radiusPx * 0.22 + shadowDrop,
            screenY + radiusPx * 0.3 + shadowDrop,
            radiusPx * 0.95,
            radiusPx * 0.8,
            0,
            0,
            Math.PI * 2,
        );
        ctx.fill();

        if (selected.has(unit.id)) {
            ctx.strokeStyle = '#4ade80';
            ctx.lineWidth = Math.max(1, radiusPx * 0.12);
            ctx.beginPath();
            ctx.arc(screenX, screenY, radiusPx * 1.15, 0, Math.PI * 2);
            ctx.stroke();
        }

        ctx.drawImage(
            atlas.canvas,
            atlas.sourceX(facing),
            atlas.sourceY(def.sprite, atlas.colorIndex(color)),
            atlas.cell,
            atlas.cell,
            screenX - size / 2,
            screenY - size / 2,
            size,
            size,
        );

        /*
         * **פס בריאות רק על מה שאתה רואה עכשיו.** ה-hp של יחידה שיצאה מהטווח
         * ממשיך להשתנות בסימולציה, ופס שמתעדכן על ידיעה ישנה הוא בדיוק סוג
         * המידע שאין לך — ובנוסף היה הדבר היחיד שזז על יחידה קפואה.
         */
        if (!stale && unit.hp < def.hp) {
            drawHealthBar(ctx, screenX, screenY, radiusPx, unit.hp / def.hp);
        }

        if (fade < 1) ctx.globalAlpha = 1;
        drawn++;
    }

    return drawn;
}

function drawHealthBar(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    ratio: number,
): void {
    const width = radius * 2.2;
    const height = Math.max(2, radius * 0.16);
    const top = y - radius * 1.5;

    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillRect(x - width / 2, top, width, height);
    ctx.fillStyle = ratio > 0.6 ? '#22c55e' : ratio > 0.3 ? '#eab308' : '#ef4444';
    ctx.fillRect(x - width / 2, top, width * ratio, height);
}

/** סימון היעד של היחידות הנבחרות — משוב מיידי שאינו עובר דרך הסימולציה. */
export function drawMoveTargets(
    ctx: CanvasRenderingContext2D,
    cam: Camera,
    vp: Viewport,
    state: GameState,
    selected: ReadonlySet<UnitId>,
    mapW: number,
): void {
    ctx.strokeStyle = 'rgba(74, 222, 128, 0.55)';
    ctx.lineWidth = 1.5;

    for (const unit of state.units) {
        if (!selected.has(unit.id) || unit.mode !== UnitMode.Moving || unit.goalTile < 0) continue;
        const tileX = unit.goalTile % mapW;
        const tileY = (unit.goalTile - tileX) / mapW;
        const x = worldToScreenX(tileX * TILE + TILE / 2, cam, vp);
        const y = worldToScreenY(tileY * TILE + TILE / 2, cam, vp);
        const r = Math.max(4, cam.zoom * 0.3);

        ctx.beginPath();
        ctx.moveTo(x - r, y);
        ctx.lineTo(x + r, y);
        ctx.moveTo(x, y - r);
        ctx.lineTo(x, y + r);
        ctx.stroke();
    }
}

/**
 * מבנים.
 *
 * מצוירים לפני היחידות — יחידה שעומדת ליד מבנה צריכה להיראות מעליו, לא מתחתיו.
 */
export function drawBuildings(
    ctx: CanvasRenderingContext2D,
    cam: Camera,
    vp: Viewport,
    state: GameState,
    playerColors: readonly string[],
    fog?: FogView,
): void {
    const scale = cam.zoom / TILE;

    for (const building of state.buildings) {
        if (building.hp <= 0) continue;
        const def = buildingDef(building.type);
        /*
         * מבנה של היריב מצויר גם כשאינו נראה כרגע — ובלבד שהאריח **נחשף** פעם.
         * זה הזיכרון של ערפל הקרב: מבנה אינו זז, ולכן "ראיתי שם מפעל" נשאר נכון.
         * ליחידות ההפך בדיוק — ראו `drawUnits`.
         */
        if (fog && building.owner !== fog.localPlayer) {
            /*
             * מבנה אויב מצויר **מהזיכרון** ולא מהמצב: מה שהשחקן יודע, לא מה
             * שקורה. הרשומה נכנסת לזיכרון כשראית אותה ויוצאת כשחזרת וראית
             * שהיא איננה — ראו `render/memory.ts`.
             */
            if (fog.remembered && !fog.remembered.has(building.id)) continue;
            if (!fog.remembered) {
                const tile = building.tileY * fog.mapWidth + building.tileX;
                if (fog.explored[tile] !== 1) continue;
            }
        }
        const x = worldToScreenX(building.tileX * TILE, cam, vp);
        const y = worldToScreenY(building.tileY * TILE, cam, vp);
        const w = def.w * TILE * scale;
        const h = def.h * TILE * scale;
        if (x + w < 0 || y + h < 0 || x > vp.width || y > vp.height) continue;

        const color = playerColors[building.owner] ?? '#ffffff';
        /*
         * צל על הקרקע לפני המבנה — אותו כיוון קבוע כמו אצל היחידות, וזה מה
         * שמקרקע אותו במקום להשאיר אותו מרחף על הדשא.
         */
        ctx.fillStyle = 'rgba(0,0,0,0.3)';
        ctx.fillRect(x + w * 0.06, y + h * 0.08, w * 0.94, h * 0.94);

        // המרכז ולא הפינה: המנוע מצייר סביב הראשית.
        buildingSprites.draw(ctx, def.sprite, x + w / 2, y + h / 2, cam.zoom, color);

        if (building.hp < def.hp) {
            drawHealthBar(ctx, x + w / 2, y + h / 2, Math.max(w, h) / 2, building.hp / def.hp);
        }
    }
}

/**
 * שכבת ההצבה: כל האריחים שבהם אפשר להעמיד את המבנה שממתין.
 *
 * **בלי זה ההצבה היא ניחוש.** בר לא הצליח להוסיף מבנים: הוא הקיש על המפה, שום
 * דבר לא קרה, ולא הייתה שום דרך לדעת למה. `canPlace` דורש שכל אריחי המבנה יהיו
 * פנויים ועבירים **וגם** שהמבנה יהיה בטווח בנייה ממבנה קיים — שני תנאים שלא
 * נראים על המסך בכלל.
 *
 * מצויר מעל הקרקע ומתחת ליחידות, כך שהוא לא מסתיר את מה שכבר עומד שם.
 */
export function drawPlacementGrid(
    ctx: CanvasRenderingContext2D,
    cam: Camera,
    vp: Viewport,
    tiles: readonly number[],
    mapW: number,
    accent: string,
): void {
    if (tiles.length === 0) return;
    const size = cam.zoom;

    ctx.save();
    ctx.globalAlpha = 0.22;
    ctx.fillStyle = accent;
    for (const tile of tiles) {
        const tileX = tile % mapW;
        const tileY = (tile - tileX) / mapW;
        const x = worldToScreenX(tileX * TILE, cam, vp);
        const y = worldToScreenY(tileY * TILE, cam, vp);
        if (x + size < 0 || y + size < 0 || x > vp.width || y > vp.height) continue;
        // מרווח של פיקסל בין אריחים — כך נקראת רשת ולא כתם אחד גדול.
        ctx.fillRect(x + 1, y + 1, size - 2, size - 2);
    }
    ctx.restore();
}

/**
 * קליעים.
 *
 * מצוירים מהמצב ולא מהאפקטים, כי הם **כן** חלק מהסימולציה: פגז באוויר יכול
 * להחטיא מטרה שזזה, ולכן הוא ישות אמיתית ולא קישוט. השובל הוא ויזואלי בלבד.
 */
export function drawProjectiles(
    ctx: CanvasRenderingContext2D,
    cam: Camera,
    vp: Viewport,
    cur: GameState,
    prev: GameState,
    alpha: number,
    playerColors: readonly string[],
): void {
    const previous = new Map(prev.projectiles.map((p) => [p.id, p]));

    for (const projectile of cur.projectiles) {
        const before = previous.get(projectile.id);
        const worldX = before ? before.x + (projectile.x - before.x) * alpha : projectile.x;
        const worldY = before ? before.y + (projectile.y - before.y) * alpha : projectile.y;

        const x = worldToScreenX(worldX, cam, vp);
        const y = worldToScreenY(worldY, cam, vp);
        if (x < -20 || y < -20 || x > vp.width + 20 || y > vp.height + 20) continue;

        const def = projectileDef(projectile.type);
        const scale = cam.zoom / TILE;
        const color = playerColors[projectile.owner] ?? '#ffffff';

        if (def.visual === 'tracer') {
            // קו קצר בכיוון התנועה — כדור בודד לא נראה, שובל כן.
            const dx = before ? projectile.x - before.x : 0;
            const dy = before ? projectile.y - before.y : 0;
            const length = Math.hypot(dx, dy) * scale * 0.5;
            const angle = Math.atan2(dy, dx);
            ctx.strokeStyle = '#ffe08a';
            ctx.lineWidth = Math.max(1, 3 * scale * 12);
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x - Math.cos(angle) * length, y - Math.sin(angle) * length);
            ctx.stroke();
        } else {
            const radius = Math.max(1.5, 26 * scale);
            ctx.fillStyle = '#ffd27a';
            ctx.beginPath();
            ctx.arc(x, y, radius, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = color;
            ctx.lineWidth = Math.max(1, radius * 0.35);
            ctx.stroke();
        }
    }
}

/** תיבת הבחירה שהמשתמש גורר עכשיו. קואורדינטות מסך. */
export function drawSelectionBox(
    ctx: CanvasRenderingContext2D,
    box: { x0: number; y0: number; x1: number; y1: number },
): void {
    const x = Math.min(box.x0, box.x1);
    const y = Math.min(box.y0, box.y1);
    const w = Math.abs(box.x1 - box.x0);
    const h = Math.abs(box.y1 - box.y0);

    ctx.fillStyle = 'rgba(74, 222, 128, 0.12)';
    ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = '#4ade80';
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, y + 0.5, w, h);
}
